import { useState } from 'react';
import api from '../api/axios';
import { toast } from 'react-toastify';

// Statuses where the workflow is still open. Must mirror OPEN_STATUSES in
// backend/services/approvalService.js — the server is the real gate, this only
// avoids showing a composer that is guaranteed to bounce.
const OPEN_STATUSES = ['pending', 'approved', 'in_progress', 'rejected', 'needs_info'];

const ROLE_LABEL = {
  approver: 'Approver',
  it_support: 'IT',
  admin: 'Admin',
};

const ROLE_COLOR = {
  approver: '#4f6ef7',
  it_support: '#ea580c',
  admin: '#0d1a6e',
};

const stamp = v => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/**
 * The discussion thread on a request. Any approver, IT or admin can post,
 * whether or not they still hold an open approval ticket — commenting is
 * deliberately independent of the approve/reject decision.
 */
const RequestComments = ({ request, canPost, onPosted }) => {
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const comments = request?.comments || [];
  const open = OPEN_STATUSES.includes(request?.status);
  const allowed = open;

  const submit = async e => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/requests/${request.id}/comments`, {
        source: request.source,
        body: body.trim(),
      });
      setBody('');
      // Push into the thread locally rather than refetching the whole list.
      onPosted?.({ ...data, created_at: data.created_at || new Date().toISOString() });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not post comment');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <span style={S.title}>Discussion</span>
        <span style={S.count}>{comments.length}</span>
      </div>

      {comments.length === 0 ? (
        <div style={S.empty}>No comments yet.</div>
      ) : (
        <div style={S.thread}>
          {comments.map(c => (
            <div key={c.id} style={S.msg}>
              <div style={S.msgHead}>
                <span style={S.author}>{c.author_name}</span>
                <span style={{ ...S.role, background: `${ROLE_COLOR[c.author_role] || '#64748b'}18`, color: ROLE_COLOR[c.author_role] || '#64748b' }}>
                  {ROLE_LABEL[c.author_role] || c.author_role}
                </span>
                <span style={S.time}>{stamp(c.created_at)}</span>
              </div>
              <div style={S.body}>{c.body}</div>
            </div>
          ))}
        </div>
      )}

      {allowed ? (
        <form onSubmit={submit} style={S.composer}>
          <textarea
            style={S.textarea}
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Add a comment for the requester and the other approvers..."
          />
          <button type="submit" style={{ ...S.btn, opacity: sending || !body.trim() ? 0.5 : 1 }} disabled={sending || !body.trim()}>
            {sending ? 'Posting…' : 'Post comment'}
          </button>
        </form>
      ) : (
        <div style={S.locked}>
          {`Comments are closed — this request is ${String(request?.status || '').replace('_', ' ')}.`}
        </div>
      )}
    </div>
  );
};

const S = {
  wrap: {
    background: '#f8faff', border: '1px solid #e5e9f5', borderRadius: 12,
    padding: '12px 14px', marginBottom: 14,
  },
  head: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  title: { fontSize: 10, fontWeight: 800, color: '#64748b', letterSpacing: 0.8, textTransform: 'uppercase' },
  count: {
    background: '#eef1f7', color: '#64748b', fontSize: 10.5, fontWeight: 800,
    padding: '1px 7px', borderRadius: 9,
  },
  empty: { fontSize: 12.5, color: '#94a3b8', padding: '4px 0 10px' },
  thread: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10, maxHeight: 260, overflowY: 'auto' },
  msg: { background: '#fff', border: '1px solid #eef1f7', borderRadius: 10, padding: '9px 11px' },
  msgHead: { display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 },
  author: { fontSize: 12.5, fontWeight: 800, color: '#0B1554' },
  role: { fontSize: 9, fontWeight: 800, letterSpacing: 0.4, padding: '1px 6px', borderRadius: 4 },
  time: { marginLeft: 'auto', fontSize: 10.5, color: '#94a3b8', whiteSpace: 'nowrap' },
  body: { fontSize: 12.5, color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  composer: { display: 'flex', gap: 8, alignItems: 'flex-end' },
  textarea: {
    flex: 1, minHeight: 58, maxHeight: 160, padding: '9px 11px',
    border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13,
    color: '#0B1554', background: '#fff', boxSizing: 'border-box', resize: 'vertical',
    fontFamily: 'inherit',
  },
  btn: {
    padding: '10px 16px', color: '#fff', border: 'none', borderRadius: 8,
    fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
    background: 'linear-gradient(135deg,#0d1a6e,#4f6ef7)',
  },
  locked: {
    fontSize: 11.5, color: '#64748b', background: '#f1f5f9',
    borderRadius: 8, padding: '8px 11px',
  },
};

export default RequestComments;
